import { cva, type VariantProps } from 'class-variance-authority'
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link IconButton}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-8 w-8), md (h-10 w-10, default), lg (h-12 w-12)
 * - Square aspect ratio for icons
 * - Same color variants as Button: primary, secondary, danger, success, muted
 * - Focus ring uses the brand primary color
 *
 * @example
 * ```tsx
 * const classes = iconButtonVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center justify-center ... h-10 w-10 ..."
 * ```
 */
export const iconButtonVariants = cva(
	'inline-flex items-center justify-center font-normal transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:opacity-50 disabled:pointer-events-none rounded-ds-md',
	{
		variants: {
			variant: {
				primary: 'bg-ds-primary text-ds-primary-foreground hover:bg-ds-primary-hover',
				secondary: 'bg-ds-secondary text-ds-text hover:bg-ds-surface-elevated border border-ds-border',
				danger: 'bg-ds-danger text-ds-danger-foreground hover:opacity-90',
				success: 'bg-ds-success text-ds-success-foreground hover:opacity-90',
				muted: 'bg-ds-surface text-ds-text-muted hover:bg-ds-surface-elevated border border-ds-border',
			},
			size: {
				sm: 'h-8 w-8 text-ds-sm',
				md: 'h-10 w-10 text-ds-base',
				lg: 'h-12 w-12 text-ds-lg',
			},
		},
		defaultVariants: { variant: 'primary', size: 'md' },
	},
)

/**
 * Props for the {@link IconButton} component.
 *
 * Extends React Aria's ButtonProps with CVA variant props.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'muted'
 * @property size - Button size: 'sm' (h-8 w-8) | 'md' (h-10 w-10, default) | 'lg' (h-12 w-12)
 * @property className - Optional additional Tailwind classes
 * @property children - Icon element (typically from lucide-react)
 * @property aria-label - REQUIRED accessible label for screen readers
 * @property onPress - Click handler (React Aria pattern)
 * @property isDisabled - Disabled state (React Aria pattern)
 *
 * @example
 * ```tsx
 * <IconButton variant="primary" size="md" aria-label="Close" onPress={handleClose}>
 *   <X size={20} />
 * </IconButton>
 * ```
 */
export interface IconButtonProps extends Omit<AriaButtonProps, 'className'>, VariantProps<typeof iconButtonVariants> {
	className?: string
	'aria-label': string // Required for accessibility
}

/**
 * Accessible icon-only button primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** MUST include `aria-label` prop for screen readers.
 *   Keyboard navigation, focus management provided by React Aria's `<Button>`.
 * - **Styling:** All colors and spacing resolve to design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Square aspect ratio:** Width equals height for consistent icon centering.
 * - **Variants:** Primary (brand primary), secondary, danger, success, muted.
 * - **Sizes:** Small (h-8 w-8), medium (h-10 w-10, default), large (h-12 w-12).
 * - **Icons:** Use lucide-react icons (e.g., `<X />`, `<Menu />`, `<Settings />`).
 *
 * @example Basic usage
 * ```tsx
 * import { IconButton } from '@atlas/ui-primitives'
 * import { X, Menu, Settings } from 'lucide-react'
 *
 * function MyToolbar() {
 *   return (
 *     <div>
 *       <IconButton variant="primary" aria-label="Close" onPress={handleClose}>
 *         <X size={20} />
 *       </IconButton>
 *       <IconButton variant="muted" aria-label="Menu" onPress={handleMenu}>
 *         <Menu size={20} />
 *       </IconButton>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With disabled state
 * ```tsx
 * <IconButton variant="danger" aria-label="Delete" isDisabled>
 *   <Trash2 size={20} />
 * </IconButton>
 * ```
 *
 * @see {@link iconButtonVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/icon-button/icon-button.stories.tsx for visual examples
 */
export function IconButton({ variant, size, className, ...props }: IconButtonProps) {
	return <AriaButton {...props} className={cn(iconButtonVariants({ variant, size }), className)} />
}
